import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/v2/Toast";
import { SettingsSection } from "@/components/v2/account/SettingsSection";
import { EmailPreviewer } from "@/components/v2/EmailPreviewer";
import {
  TENANT_NOTIFICATION_DEFAULTS,
  useTenantNotificationSettings,
  useUpdateTenantNotificationSettings,
  type TenantNotificationSettings,
} from "@/lib/tenant-notification-settings";

type Settings = Required<TenantNotificationSettings>;

export function WorkspaceNotifications() {
  const toast = useToast();
  const { data, isLoading } = useTenantNotificationSettings();
  const update = useUpdateTenantNotificationSettings();

  const [settings, setSettings] = useState<Settings>(
    TENANT_NOTIFICATION_DEFAULTS,
  );
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setSettings({ ...TENANT_NOTIFICATION_DEFAULTS, ...data });
      setDirty(false);
    }
  }, [data]);

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const replyToInvalid =
    !!settings.replyToEmail && !/^\S+@\S+\.\S+$/.test(settings.replyToEmail);

  const save = async () => {
    if (replyToInvalid) {
      toast.error("Invalid reply-to email");
      return;
    }
    try {
      await update.mutateAsync({
        senderName: settings.senderName?.trim() || null,
        replyToEmail: settings.replyToEmail?.trim() || null,
        emailAdminsOnNewBooking: settings.emailAdminsOnNewBooking,
        weeklyDigestEnabled: settings.weeklyDigestEnabled,
      });
      setDirty(false);
      toast.success("Workspace notifications saved");
    } catch (err) {
      toast.error(
        "Couldn't save",
        err instanceof Error ? err.message : "Try again.",
      );
    }
  };

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Sender identity"
        description="How outgoing emails appear to recipients. Defaults use your workspace name and the platform support address."
      >
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="sender-name" className="text-[13px] font-semibold">
              Sender name
            </Label>
            <Input
              id="sender-name"
              value={settings.senderName ?? ""}
              onChange={(e) => set("senderName", e.target.value)}
              placeholder="e.g. Acme Studio"
            />
            <p className="text-xs text-muted-foreground">
              Shown as the &ldquo;From&rdquo; display name. Empty falls back to the
              workspace name.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="reply-to" className="text-[13px] font-semibold">
              Reply-to email
            </Label>
            <Input
              id="reply-to"
              type="email"
              value={settings.replyToEmail ?? ""}
              onChange={(e) => set("replyToEmail", e.target.value)}
              placeholder="support@your-domain.com"
            />
            <p className="text-xs text-muted-foreground">
              Replies route here instead of the platform support inbox.
            </p>
            {replyToInvalid && (
              <p className="text-xs text-destructive">
                Enter a valid email address.
              </p>
            )}
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Routing"
        description="Who else gets notified when something important happens."
      >
        <div className="divide-y">
          <Toggle
            label="Email all admins on new bookings"
            desc="When a customer submits a booking, every OWNER/ADMIN gets a copy. Staff still see it in-app either way."
            checked={settings.emailAdminsOnNewBooking}
            onChange={(v) => set("emailAdminsOnNewBooking", v)}
          />
          <Toggle
            label="Weekly digest enabled"
            desc="Sends each team member a Monday morning summary of last week's bookings, payments, and revenue. Honors per-user preferences."
            checked={settings.weeklyDigestEnabled}
            onChange={(v) => set("weeklyDigestEnabled", v)}
          />
        </div>
      </SettingsSection>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={
            !dirty || isLoading || update.isPending || replyToInvalid
          }
        >
          {update.isPending ? "Saving…" : "Save settings"}
        </Button>
      </div>

      <SettingsSection
        title="Preview"
        description="See exactly what recipients will receive — using your workspace logo and brand color, with sample data."
      >
        <div className="px-5 py-4">
          <EmailPreviewer />
        </div>
      </SettingsSection>
    </div>
  );
}

function Toggle({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 px-5 py-3 cursor-pointer">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </label>
  );
}
